import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './MobileControls.css';

const MobileControls = ({ rufflePlayer }) => {
  const [showFullKeyboard, setShowFullKeyboard] = useState(false);
  const [pressedKeys, setPressedKeys] = useState({});
  
  // Function to send keyboard events to the Ruffle player
  const sendKeyEvent = (key, type) => {
    if (!rufflePlayer) return;
    
    // Ensure Ruffle has focus first
    if (typeof rufflePlayer.focus === 'function') {
      rufflePlayer.focus();
    }
    
    // Get the proper code for the key
    const keyInfo = getKeyInfo(key);
    
    // Create keyboard event
    const event = new KeyboardEvent(type, {
      key: keyInfo.key,
      code: keyInfo.code,
      keyCode: keyInfo.keyCode,
      which: keyInfo.keyCode,
      bubbles: true,
      cancelable: true
    });
    
    // Dispatch event to the player itself
    rufflePlayer.dispatchEvent(event);
  };
  
  // Helper to get the key info for a given key
  const getKeyInfo = (key) => {
    const keyMap = {
      'Q': { key: 'q', code: 'KeyQ', keyCode: 81 },
      'W': { key: 'w', code: 'KeyW', keyCode: 87 },
      'E': { key: 'e', code: 'KeyE', keyCode: 69 },
      'R': { key: 'r', code: 'KeyR', keyCode: 82 },
      'T': { key: 't', code: 'KeyT', keyCode: 84 },
      'Y': { key: 'y', code: 'KeyY', keyCode: 89 },
      'U': { key: 'u', code: 'KeyU', keyCode: 85 },
      'I': { key: 'i', code: 'KeyI', keyCode: 73 },
      'O': { key: 'o', code: 'KeyO', keyCode: 79 },
      'P': { key: 'p', code: 'KeyP', keyCode: 80 },
      'A': { key: 'a', code: 'KeyA', keyCode: 65 },
      'S': { key: 's', code: 'KeyS', keyCode: 83 },
      'D': { key: 'd', code: 'KeyD', keyCode: 68 },
      'F': { key: 'f', code: 'KeyF', keyCode: 70 },
      'G': { key: 'g', code: 'KeyG', keyCode: 71 },
      'H': { key: 'h', code: 'KeyH', keyCode: 72 },
      'J': { key: 'j', code: 'KeyJ', keyCode: 74 },
      'K': { key: 'k', code: 'KeyK', keyCode: 75 },
      'L': { key: 'l', code: 'KeyL', keyCode: 76 },
      'Z': { key: 'z', code: 'KeyZ', keyCode: 90 },
      'X': { key: 'x', code: 'KeyX', keyCode: 88 },
      'C': { key: 'c', code: 'KeyC', keyCode: 67 },
      'V': { key: 'v', code: 'KeyV', keyCode: 86 },
      'B': { key: 'b', code: 'KeyB', keyCode: 66 },
      'N': { key: 'n', code: 'KeyN', keyCode: 78 },
      'M': { key: 'm', code: 'KeyM', keyCode: 77 },
      '↑': { key: 'ArrowUp', code: 'ArrowUp', keyCode: 38 },
      '←': { key: 'ArrowLeft', code: 'ArrowLeft', keyCode: 37 },
      '→': { key: 'ArrowRight', code: 'ArrowRight', keyCode: 39 },
      '↓': { key: 'ArrowDown', code: 'ArrowDown', keyCode: 40 },
      'Enter': { key: 'Enter', code: 'Enter', keyCode: 13 },
      'Esc': { key: 'Escape', code: 'Escape', keyCode: 27 },
      '空格': { key: ' ', code: 'Space', keyCode: 32 },
      '1': { key: '1', code: 'Digit1', keyCode: 49 },
      '2': { key: '2', code: 'Digit2', keyCode: 50 },
      '3': { key: '3', code: 'Digit3', keyCode: 51 },
      '4': { key: '4', code: 'Digit4', keyCode: 52 },
      '5': { key: '5', code: 'Digit5', keyCode: 53 },
      '6': { key: '6', code: 'Digit6', keyCode: 54 },
      '7': { key: '7', code: 'Digit7', keyCode: 55 },
      '8': { key: '8', code: 'Digit8', keyCode: 56 },
      '9': { key: '9', code: 'Digit9', keyCode: 57 },
      '0': { key: '0', code: 'Digit0', keyCode: 48 },
      'Space': { key: ' ', code: 'Space', keyCode: 32 },
      'Shift': { key: 'Shift', code: 'ShiftLeft', keyCode: 16 },
      'Ctrl': { key: 'Control', code: 'ControlLeft', keyCode: 17 },
      'Alt': { key: 'Alt', code: 'AltLeft', keyCode: 18 },
      'Tab': { key: 'Tab', code: 'Tab', keyCode: 9 }
    };
    
    return keyMap[key] || { key: key, code: key, keyCode: key.charCodeAt(0) };
  };
  
  // Function to handle button press
  const handleButtonPress = (key) => {
    if (key === '全键盘') {
      setShowFullKeyboard(!showFullKeyboard);
      return;
    }
    
    // Update pressed keys state
    setPressedKeys(prev => ({ ...prev, [key]: true }));
    sendKeyEvent(key, 'keydown');
  };
  
  // Function to handle button release
  const handleButtonRelease = (key) => {
    if (key === '全键盘') return;
    
    // Clear pressed key state
    setPressedKeys(prev => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
    
    sendKeyEvent(key, 'keyup');
  };
  
  // Clean up any stuck keys when component unmounts
  useEffect(() => {
    return () => {
      // Release all pressed keys
      Object.keys(pressedKeys).forEach(key => {
        if (key !== '全键盘') {
          sendKeyEvent(key, 'keyup');
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pressedKeys]); // sendKeyEvent is intentionally excluded
  
  // Additional keys for the full keyboard mode - more useful keys for Flash games
  const additionalKeys = showFullKeyboard ? [
    ['T', 'Y', 'U', 'I', 'O', 'P'],
    ['F', 'G', 'H', 'J', 'K', 'L'],
    ['V', 'B', 'N', 'M', 'Tab', 'Shift'],
    ['1', '2', '3', '4', '5', '0']
  ] : [];
  
  return (
    <div className="mobile-controls">
      <div className="controls-row">
        <button 
          onTouchStart={() => handleButtonPress('Q')}
          onTouchEnd={() => handleButtonRelease('Q')}
          onMouseDown={() => handleButtonPress('Q')}
          onMouseUp={() => handleButtonRelease('Q')}
          onMouseLeave={() => pressedKeys['Q'] && handleButtonRelease('Q')}
        >Q</button>
        <button 
          onTouchStart={() => handleButtonPress('W')}
          onTouchEnd={() => handleButtonRelease('W')}
          onMouseDown={() => handleButtonPress('W')}
          onMouseUp={() => handleButtonRelease('W')}
          onMouseLeave={() => pressedKeys['W'] && handleButtonRelease('W')}
        >W</button>
        <button 
          onTouchStart={() => handleButtonPress('E')}
          onTouchEnd={() => handleButtonRelease('E')}
          onMouseDown={() => handleButtonPress('E')}
          onMouseUp={() => handleButtonRelease('E')}
          onMouseLeave={() => pressedKeys['E'] && handleButtonRelease('E')}
        >E</button>
        <button 
          onTouchStart={() => handleButtonPress('R')}
          onTouchEnd={() => handleButtonRelease('R')}
          onMouseDown={() => handleButtonPress('R')}
          onMouseUp={() => handleButtonRelease('R')}
          onMouseLeave={() => pressedKeys['R'] && handleButtonRelease('R')}
        >R</button>
        <button 
          onTouchStart={() => handleButtonPress('↑')}
          onTouchEnd={() => handleButtonRelease('↑')}
          onMouseDown={() => handleButtonPress('↑')}
          onMouseUp={() => handleButtonRelease('↑')}
          onMouseLeave={() => pressedKeys['↑'] && handleButtonRelease('↑')}
        >↑</button>
        <button 
          onTouchStart={() => handleButtonPress('Esc')}
          onTouchEnd={() => handleButtonRelease('Esc')}
          onMouseDown={() => handleButtonPress('Esc')}
          onMouseUp={() => handleButtonRelease('Esc')}
          onMouseLeave={() => pressedKeys['Esc'] && handleButtonRelease('Esc')}
        >Esc</button>
      </div>
      
      <div className="controls-row">
        <button 
          onTouchStart={() => handleButtonPress('A')}
          onTouchEnd={() => handleButtonRelease('A')}
          onMouseDown={() => handleButtonPress('A')}
          onMouseUp={() => handleButtonRelease('A')}
          onMouseLeave={() => pressedKeys['A'] && handleButtonRelease('A')}
        >A</button>
        <button 
          onTouchStart={() => handleButtonPress('S')}
          onTouchEnd={() => handleButtonRelease('S')}
          onMouseDown={() => handleButtonPress('S')}
          onMouseUp={() => handleButtonRelease('S')}
          onMouseLeave={() => pressedKeys['S'] && handleButtonRelease('S')}
        >S</button>
        <button 
          onTouchStart={() => handleButtonPress('D')}
          onTouchEnd={() => handleButtonRelease('D')}
          onMouseDown={() => handleButtonPress('D')}
          onMouseUp={() => handleButtonRelease('D')}
          onMouseLeave={() => pressedKeys['D'] && handleButtonRelease('D')}
        >D</button>
        <button 
          onTouchStart={() => handleButtonPress('←')}
          onTouchEnd={() => handleButtonRelease('←')}
          onMouseDown={() => handleButtonPress('←')}
          onMouseUp={() => handleButtonRelease('←')}
          onMouseLeave={() => pressedKeys['←'] && handleButtonRelease('←')}
        >←</button>
        <button 
          onTouchStart={() => handleButtonPress('Enter')}
          onTouchEnd={() => handleButtonRelease('Enter')}
          onMouseDown={() => handleButtonPress('Enter')}
          onMouseUp={() => handleButtonRelease('Enter')}
          onMouseLeave={() => pressedKeys['Enter'] && handleButtonRelease('Enter')}
        >Enter</button>
        <button 
          onTouchStart={() => handleButtonPress('→')}
          onTouchEnd={() => handleButtonRelease('→')}
          onMouseDown={() => handleButtonPress('→')}
          onMouseUp={() => handleButtonRelease('→')}
          onMouseLeave={() => pressedKeys['→'] && handleButtonRelease('→')}
        >→</button>
      </div>
      
      <div className="controls-row">
        <button 
          onTouchStart={() => handleButtonPress('Z')}
          onTouchEnd={() => handleButtonRelease('Z')}
          onMouseDown={() => handleButtonPress('Z')}
          onMouseUp={() => handleButtonRelease('Z')}
          onMouseLeave={() => pressedKeys['Z'] && handleButtonRelease('Z')}
        >Z</button>
        <button 
          onTouchStart={() => handleButtonPress('X')}
          onTouchEnd={() => handleButtonRelease('X')}
          onMouseDown={() => handleButtonPress('X')}
          onMouseUp={() => handleButtonRelease('X')}
          onMouseLeave={() => pressedKeys['X'] && handleButtonRelease('X')}
        >X</button>
        <button 
          onTouchStart={() => handleButtonPress('C')}
          onTouchEnd={() => handleButtonRelease('C')}
          onMouseDown={() => handleButtonPress('C')}
          onMouseUp={() => handleButtonRelease('C')}
          onMouseLeave={() => pressedKeys['C'] && handleButtonRelease('C')}
        >C</button>
        <button 
          onTouchStart={() => handleButtonPress('空格')}
          onTouchEnd={() => handleButtonRelease('空格')}
          onMouseDown={() => handleButtonPress('空格')}
          onMouseUp={() => handleButtonRelease('空格')}
          onMouseLeave={() => pressedKeys['空格'] && handleButtonRelease('空格')}
        >空格</button>
        <button 
          onTouchStart={() => handleButtonPress('↓')}
          onTouchEnd={() => handleButtonRelease('↓')}
          onMouseDown={() => handleButtonPress('↓')}
          onMouseUp={() => handleButtonRelease('↓')}
          onMouseLeave={() => pressedKeys['↓'] && handleButtonRelease('↓')}
        >↓</button>
        <button 
          onClick={() => handleButtonPress('全键盘')}
          className={showFullKeyboard ? 'active' : ''}
        >全键盘</button>
      </div>
      
      {/* Additional keys that show when 全键盘 (full keyboard) is pressed */}
      {showFullKeyboard && (
        <div className="extended-keyboard">
          {additionalKeys.map((row, rowIndex) => (
            <div key={`row-${rowIndex}`} className="controls-row">
              {row.map((key) => (
                <button 
                  key={key}
                  onTouchStart={() => handleButtonPress(key)}
                  onTouchEnd={() => handleButtonRelease(key)}
                  onMouseDown={() => handleButtonPress(key)}
                  onMouseUp={() => handleButtonRelease(key)}
                  onMouseLeave={() => pressedKeys[key] && handleButtonRelease(key)}
                >{key}</button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

MobileControls.propTypes = {
  rufflePlayer: PropTypes.object
};

export default MobileControls; 